/**
 * AI Ops local evidence plane — browser/device SoR only (SoT 2026-08-08 §4).
 *
 * Server must never receive pack bodies from this module. Thin audit links via
 * content_hash / local_pack_id only.
 */
const DB_KEY = "croc_ai_local_evidence_v1";
const MAX_PACKS = 80;

function _safeParse(raw) {
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function _now() {
  return Math.floor(Date.now() / 1000);
}

function _id() {
  return "le-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/** Async-friendly hash (FNV-1a) — not crypto SoT, only link integrity. */
export function hashLocalPayload(obj) {
  const s = typeof obj === "string" ? obj : JSON.stringify(obj || {});
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ("00000000" + (h >>> 0).toString(16)).slice(-8);
}

/**
 * @param {"live"|"operation"|"response"} kind
 * @param {{ incidentId?: string, verb?: string, payload?: any }} pack
 */
export function appendLocalEvidence(kind, pack = {}) {
  if (typeof localStorage === "undefined") {
    return { ok: false, reason: "no_local_store" };
  }
  const k = String(kind || "operation");
  if (!["live", "operation", "response"].includes(k)) {
    return { ok: false, reason: "kind_invalid" };
  }
  const entry = {
    local_pack_id: _id(),
    kind: k,
    incident_id: String(pack.incidentId || "").slice(0, 128),
    verb: String(pack.verb || "").slice(0, 64),
    ts: _now(),
    payload: pack.payload == null ? null : pack.payload,
  };
  entry.content_hash = hashLocalPayload({
    k: entry.kind,
    i: entry.incident_id,
    v: entry.verb,
    p: entry.payload,
    t: entry.ts,
  });
  try {
    const rows = _safeParse(localStorage.getItem(DB_KEY));
    rows.unshift(entry);
    while (rows.length > MAX_PACKS) rows.pop();
    localStorage.setItem(DB_KEY, JSON.stringify(rows));
    return {
      ok: true,
      local_pack_id: entry.local_pack_id,
      content_hash: entry.content_hash,
      server_store: false,
    };
  } catch (_e) {
    return { ok: false, reason: "local_store_failed" };
  }
}

export function listLocalEvidence({ incidentId = "", limit = 20 } = {}) {
  if (typeof localStorage === "undefined") return [];
  const rows = _safeParse(localStorage.getItem(DB_KEY));
  const id = String(incidentId || "").trim();
  const filtered = id
    ? rows.filter((r) => r && String(r.incident_id || "") === id)
    : rows;
  return filtered.slice(0, Math.max(1, Math.min(100, Number(limit) || 20)));
}

export function clearLocalEvidence() {
  if (typeof localStorage === "undefined") return false;
  try {
    localStorage.removeItem(DB_KEY);
    return true;
  } catch {
    return false;
  }
}

export const LOCAL_EVIDENCE_SOT = Object.freeze({
  plane: "local_only",
  server_blob_forbidden: true,
  kinds: ["live", "operation", "response"],
});
